import React, { useState, useCallback } from 'react';
import Modal from '../../../BaseComponents/Modal';
import { Formik } from 'formik';
import TextArea from '../../../BaseComponents/TextArea';
import './ConditionModal.scss';
import Button from '../../../BaseComponents/Button';
import TextInput from '../../../BaseComponents/TextInput';
import {
  CreateConditionRequest,
  ConditionWithDetails,
  BodySystem,
  Biomarker,
  AssociatedFbsCondition,
  getFbsConditionLabel,
} from '../../../../api/conditions';
import { createCondition, updateCondition } from '../../../../api/conditions';
import { ReactComponent as BinIcon } from '../../../../assets/icons/TrashBin2.svg';
import { toast } from 'react-toastify';
import MultiSelect from '../../../BaseComponents/MultiSelect';
import { getBodySystemsList } from '../../../../api/body-systems';
import { getBiomarkersList } from '../../../../api/biomarkers';
import { getMriConditionsList, MriCondition } from '../../../../api/mriRadiology';

type ConditionsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  conditionToEdit?: ConditionWithDetails | null;
  onConditionSaved?: () => void;
  onDeleteCondition?: (conditionId: number) => void;
};

interface ConditionFormValues {
  name: string;
  description: string;
  body_system_ids: number[];
  biomarker_ids: number[];
  mri_condition_ids: number[];
}

const ConditionsModal: React.FC<ConditionsModalProps> = ({ 
  isOpen, 
  onClose, 
  conditionToEdit, 
  onConditionSaved, 
  onDeleteCondition 
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<any>(null);
  const [bodySystemOptions, setBodySystemOptions] = useState<{ key: string; value: string }[]>([]);
  const [biomarkerOptions, setBiomarkerOptions] = useState<{ key: string; value: string }[]>([]);
  const [fbsConditionOptions, setFbsConditionOptions] = useState<{ key: string; value: string }[]>([]);
  const [selectedBodySystems, setSelectedBodySystems] = useState<{ key: string; value: string }[]>([]);
  const [selectedBiomarkers, setSelectedBiomarkers] = useState<{ key: string; value: string }[]>([]);
  const [selectedFbsConditions, setSelectedFbsConditions] = useState<{ key: string; value: string }[]>([]);
  const [bodySystemError, setBodySystemError] = useState<boolean>(false);

  // Load options when modal opens
  React.useEffect(() => {
    if (isOpen) {
      loadOptions();
    }
  }, [isOpen]);

  const loadOptions = async () => {
    try {
      const [bodySystemsResponse, biomarkersResponse, mriConditionsResponse] = await Promise.all([
        getBodySystemsList(),
        getBiomarkersList(),
        getMriConditionsList()
      ]);
      
      const bodySystemOptions = (bodySystemsResponse.body_systems || []).map((bs: BodySystem) => ({
        key: bs.id.toString(),
        value: bs.title
      }));
      const biomarkerOptions = (biomarkersResponse.biomarkers || []).map((bm: Biomarker) => ({
        key: bm.id.toString(),
        value: bm.name
      }));
      const fbsConditionOptions = (mriConditionsResponse.items || []).map((mc: MriCondition) => ({
        key: mc.id.toString(),
        value: getFbsConditionLabel(mc)
      }));
      
      setBodySystemOptions(bodySystemOptions);
      setBiomarkerOptions(biomarkerOptions);
      setFbsConditionOptions(fbsConditionOptions);
    } catch (error) {
      console.error('Error loading options:', error);
    }
  };

  // Initialize selected options when conditionToEdit changes
  React.useEffect(() => {
    if (conditionToEdit) {
      const selectedBodySystems = conditionToEdit.body_systems?.map((bs: BodySystem) => ({
        key: bs.id.toString(),
        value: bs.title
      })) ?? [];
      const selectedBiomarkers = conditionToEdit.biomarkers?.map((bm: Biomarker) => ({
        key: bm.id.toString(),
        value: bm.name
      })) ?? [];
      const selectedFbsConditions = conditionToEdit.mri_conditions?.map((mc: AssociatedFbsCondition) => ({
        key: mc.id.toString(),
        value: getFbsConditionLabel(mc)
      })) ?? [];
      
      setSelectedBodySystems(selectedBodySystems);
      setSelectedBiomarkers(selectedBiomarkers);
      setSelectedFbsConditions(selectedFbsConditions);
    } else {
      setSelectedBodySystems([]);
      setSelectedBiomarkers([]);
      setSelectedFbsConditions([]);
    }
  }, [conditionToEdit]);

  const getDefaultValues = (): ConditionFormValues => {
    if (conditionToEdit) {
      return {
        name: conditionToEdit.name ?? '',
        description: conditionToEdit.description ?? '',
        body_system_ids: conditionToEdit.body_systems?.map((bs: BodySystem) => bs.id) ?? [],
        biomarker_ids: conditionToEdit.biomarkers?.map((bm: Biomarker) => bm.id) ?? [],
        mri_condition_ids: conditionToEdit.mri_conditions?.map((mc: AssociatedFbsCondition) => mc.id) ?? []
      };
    }
    return {
      name: '',
      description: '',
      body_system_ids: [],
      biomarker_ids: [],
      mri_condition_ids: []
    };
  };

  const handleBodySystemChange = useCallback((option: { key: string; value: string }, name: string, setFieldValue: any) => {
    setSelectedBodySystems(prev => {
      const existingIndex = prev.findIndex(bs => bs.key === option.key);
      let newBodySystems: { key: string; value: string }[];
      if (existingIndex >= 0) {
        newBodySystems = prev.filter((_, index) => index !== existingIndex);
      } else {
        newBodySystems = [...prev, option];
      }
      setFieldValue('body_system_ids', newBodySystems.map(bs => parseInt(bs.key)));
      return newBodySystems;
    });
  }, []);

  const handleBiomarkerChange = useCallback((option: { key: string; value: string }, name: string, setFieldValue: any) => {
    setSelectedBiomarkers(prev => {
      const existingIndex = prev.findIndex(bm => bm.key === option.key);
      let newBiomarkers: { key: string; value: string }[];
      if (existingIndex >= 0) {
        newBiomarkers = prev.filter((_, index) => index !== existingIndex);
      } else {
        newBiomarkers = [...prev, option];
      }
      setFieldValue('biomarker_ids', newBiomarkers.map(bm => parseInt(bm.key)));
      return newBiomarkers;
    });
  }, []);

  const handleFbsConditionChange = useCallback((option: { key: string; value: string }, name: string, setFieldValue: any) => {
    setSelectedFbsConditions(prev => {
      const existingIndex = prev.findIndex(fc => fc.key === option.key);
      let newFbsConditions: { key: string; value: string }[];
      if (existingIndex >= 0) {
        newFbsConditions = prev.filter((_, index) => index !== existingIndex);
      } else {
        newFbsConditions = [...prev, option];
      }
      setFieldValue('mri_condition_ids', newFbsConditions.map(fc => parseInt(fc.key)));
      return newFbsConditions;
    });
  }, []);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={conditionToEdit ? 'Edit Medical Condition' : 'Add Medical Condition'}
      width="800px"
    >
      <Formik
        key={conditionToEdit?.id || 'new'}
        initialValues={getDefaultValues()}
        enableReinitialize={true}
        onSubmit={async (values) => {
          // Validate that at least one body system is selected
          if (!values.body_system_ids || values.body_system_ids.length === 0) {
            setBodySystemError(true);
            toast.error('Please select at least one body system');
            return;
          }
          setBodySystemError(false);

          setIsLoading(true);
          try {
            const requestObject: CreateConditionRequest = {
              name: values.name?.trim() || null, // Send null if empty or whitespace
              description: values.description?.trim() || null, // Send null if empty or whitespace
              body_system_ids: values.body_system_ids,
              biomarker_ids: values.biomarker_ids,
              mri_condition_ids: values.mri_condition_ids
            };

            if (conditionToEdit) {
              await updateCondition(conditionToEdit.id, requestObject);
              toast.success('Condition updated successfully');
            } else {
              await createCondition(requestObject);
              toast.success('Condition added successfully');
            }
            onClose();
            if (onConditionSaved) {
              onConditionSaved();
            }
          } catch (error) {
            setError(error);
            toast.error('Error adding or updating condition');
          } finally {
            setIsLoading(false);
          }
        }}
      >
        {({ handleSubmit, values, setFieldValue, errors, touched }) => (
          <form onSubmit={handleSubmit} className="condition-modal-form">
            <TextInput
              name="name"
              title="Name"
              placeholder="Enter name of the condition"
              value={values.name}
              onChange={(e) => setFieldValue('name', e.target.value)}
              isError={false}
            />
            
            <div className="form-field">
              <MultiSelect
                name="body_systems"
                options={bodySystemOptions}
                selectedOptions={selectedBodySystems}
                onChange={(option: { key: string; value: string }, name: string) => {
                  handleBodySystemChange(option, name, setFieldValue);
                  setBodySystemError(false); // Clear error when user makes a selection
                }}
                placeholder="Choose body system(s)"
                title="Body system(s) *"
                isError={bodySystemError}
                useMenuPortal
              />
              {bodySystemError && (
                <div className="error-message">Please select at least one body system</div>
              )}
            </div>

            <TextArea
              name="description"
              title="Description"
              placeholder="Enter description"
              value={values.description}
              onChange={(e) => setFieldValue('description', e.target.value)}
              isError={false}
            />

            <div className="form-field">
              <MultiSelect
                name="biomarkers"
                options={biomarkerOptions}
                selectedOptions={selectedBiomarkers}
                onChange={(option: { key: string; value: string }, name: string) => {
                  handleBiomarkerChange(option, name, setFieldValue);
                }}
                placeholder="Choose biomarkers (optional)"
                title="Associated biomarkers (optional)"
                isError={false}
                useMenuPortal
              />
            </div>

            <div className="form-field">
              <MultiSelect
                name="fbs_conditions"
                options={fbsConditionOptions}
                selectedOptions={selectedFbsConditions}
                onChange={(option: { key: string; value: string }, name: string) => {
                  handleFbsConditionChange(option, name, setFieldValue);
                }}
                placeholder="Choose FBS conditions (optional)"
                title="Associated FBS conditions (optional)"
                isError={false}
                useMenuPortal
              />
            </div>

            <div className="btn-wrapper">
              <Button
                text={conditionToEdit ? 'Save changes' : 'Add Medical Condition'}
                type="submit"
                isDisabled={!values.name || !values.body_system_ids || values.body_system_ids.length === 0}
                isLoading={isLoading}
              />
              {conditionToEdit && onDeleteCondition && (
                <div
                  className="delete-btn"
                  onClick={() => {
                    onDeleteCondition(conditionToEdit.id);
                  }}
                >
                  <BinIcon />
                  Delete Medical Condition
                </div>
              )}
              {error && (
                <div className="error-message">
                  Something went wrong. Please try again.
                </div>
              )}
            </div>
          </form>
        )}
      </Formik>
    </Modal>
  );
};

export default ConditionsModal;
